import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Collapse } from "react-bootstrap";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FiRefreshCw } from "react-icons/fi";
import { MdKeyboardArrowDown, MdSearch, MdRefresh, MdFileDownload } from "react-icons/md";
import { toast } from "react-toastify";
import {
  Utils,
  fetchWithAuth,
  fetchAjaxListData,
  resolveCampaignDetailBackPath,
  fetchApplicationAppId,
  detectDateRangePreset,
  bigAmountFormat,
  getAuthUserCurrency,
  formatDateTime,
  getDateRangeForPreset,
  getDefaultPageSize,
  selectStyles,
  toLocalIsoDate,
  getBrowserTimeZone,
  DATE_RANGE_PRESETS,
  filtersObjectHasAnyValue,
  exportListCsv,
  composeEventMetricLabel,
  EVENT_METRIC_OPTION,
} from "../../utils";
import DateRangePicker from "../common/DateRangePicker/DateRangePicker";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import ListPaginationBar from "../common/ListPaginationBar";
import "./engage.css";

const EMPTY_ATTR_FILTERS = {
  notify_sphere_id: "",
  phone: "",
  email: "",
  event: "",
  platform: "",
  gender: "",
  state: "",
  city: "",
};

const ATTR_FILTER_FIELDS = [
  { key: "notify_sphere_id", label: "notifySphere ID", placeholder: "Type notifySphere ID" },
  { key: "phone", label: "Phone", placeholder: "Type phone" },
  { key: "email", label: "Email", placeholder: "Type email" },
  { key: "event", label: "Event", placeholder: "Select events" },
  { key: "platform", label: "Platform", placeholder: "Select platforms" },
  { key: "gender", label: "Gender", placeholder: "Select genders" },
  { key: "state", label: "State", placeholder: "Select states" },
  { key: "city", label: "City", placeholder: "Select cities" },
];

const ATTR_FILTER_INPUT_KEYS = new Set(["notify_sphere_id", "phone", "email"]);

// Each attribution field's ajax-list type, named after its real source table rather than a
// shared "field" param: event is campaign_attribution-specific (scoped by campaign/organic);
// platform/state/city are plain app_users attributes (app-wide); gender is a generic
// app_users tag value (tag_key=gender) — reusable for any other custom tag the same way.
const ATTR_FILTER_AJAX_CONFIG = {
  event: { type: "campaign-event" },
  platform: { type: "subscription-platform" },
  gender: { type: "app-user-tag-value", extra: { field: "gender" } },
  state: { type: "app-user-state" },
  city: { type: "app-user-city" },
};

const DETAIL_LIST_PAGE_SIZE = getDefaultPageSize();
const RECENT_USERS_PAGE_SIZE = getDefaultPageSize();

/** Same client-side paging pattern as CampaignPerformanceTable (local helpers, not a separate module). */
function usePagedRows(rows, pageSize = DETAIL_LIST_PAGE_SIZE) {
  const [page, setPage] = useState(1);
  // Stable reference across renders (used as a dependency below) instead of a new [] literal
  // every render when rows is not an array.
  const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    setPage(1);
  }, [rows]);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, safePage, pageSize]);

  return { pageItems, page: safePage, setPage, total, totalPages, pageSize };
}

// Clicks/installs/signups/rates only — event-metric (booking) tiles render separately below,
// in their own labeled row, together for every configured metric (see `summary.metrics`).
const CAMPAIGN_SUMMARY_METRICS = [
  { key: "clicks", label: "Clicks", color: "#4c5fd5" },
  { key: "installs", label: "Campaign Installs", color: "#1b7a3f" },
  { key: "signups", label: "Campaign Signups", color: "#9a6700" },
  { key: "conversion_rate", label: "Click → Install %", suffix: "%", color: "#6b7785" },
  { key: "signup_rate", label: "Install → Signup %", suffix: "%", color: "#8b6b85" },
];

const ORGANIC_SUMMARY_METRICS = [
  { key: "installs", label: "Organic Installs", color: "#6b9e3f" },
  { key: "signups", label: "Organic Signups", color: "#b8860b" },
  { key: "signup_rate", label: "Install → Signup %", suffix: "%", color: "#8b6b85" },
];

function maxCount(rows, key = "count") {
  return Math.max(1, ...(rows || []).map((r) => Number(r[key] || r.total || 0)));
}

/** App user profile link — `/apps/:appId/users/:userId` */
function AppUserProfileLink({ routeAppId, appUserId, children }) {
  const navigate = useNavigate();
  if (!routeAppId || appUserId == null || appUserId === "") {
    return <>{children ?? "—"}</>;
  }
  return (
    <button
      type="button"
      className="btn btn-link p-0 campaign-perf-link"
      onClick={() => navigate(`/apps/${routeAppId}/users/${appUserId}`)}
    >
      {children}
    </button>
  );
}

function SimpleBarList({
  rows,
  valueKey = "count",
  labelKey = "label",
  revenueKey = null,
  paginationLabel,
}) {
  const { pageItems, page, setPage, total, totalPages, pageSize } = usePagedRows(rows);
  if (!rows?.length) return null;

  const max = maxCount(rows, valueKey);
  return (
    <>
      <ul className="campaign-detail-bar-list">
        {pageItems.map((row) => {
          const val = Number(row[valueKey] ?? row.total ?? 0);
          const revenue = revenueKey ? Number(row[revenueKey] || 0) : 0;
          const pct = Math.round((val / max) * 100);
          const showRevenue = revenueKey && revenue > 0;
          return (
            <li key={row.event || row[labelKey] || row.label}>
              <div className="campaign-detail-bar-row">
                <span className="campaign-detail-bar-label">{row[labelKey] || row.label}</span>
                {showRevenue ? (
                  <span className="campaign-detail-bar-value campaign-detail-bar-value--dual">
                    <span>{bigAmountFormat(val)}</span>
                    <span>{bigAmountFormat(revenue, getAuthUserCurrency())}</span>
                  </span>
                ) : (
                  <span className="campaign-detail-bar-value">{bigAmountFormat(val)}</span>
                )}
              </div>
              <div className="campaign-detail-bar-track">
                <span style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
      <ListPaginationBar
        page={page}
        setPage={setPage}
        total={total}
        totalPages={totalPages}
        pageSize={pageSize}
        ariaLabel={paginationLabel}
      />
    </>
  );
}

function EventCountsTable({ rows, paginationLabel }) {
  const { pageItems, page, setPage, total, totalPages, pageSize } = usePagedRows(rows);
  const data = Array.isArray(rows) ? rows : [];
  // Percent = this event's count / total count of ALL events (all pages, not just this one).
  const totalCount = data.length ? maxCount(data, "count") : 1;

  return (
    <div className="dashboard_app_table dashboard_app_table--list-records campaign-detail-recent-table">
      <div className="dashboard_table">
        <div className="table_scroll_wrapper">
          <div className="table_inner table_y_scroll">
            <table className="table_outer">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Count</th>
                  <th style={{ width: "35%" }}>% of total</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {!data.length ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">
                      —
                    </td>
                  </tr>
                ) : (
                  pageItems.map((row) => {
                    const revenue = Number(row.revenue || 0);
                    const val = Number(row.count ?? row.total ?? 0);
                    const pctExact = (val / totalCount) * 100;
                    const pct = Math.min(100, pctExact);
                    const pctLabel = `${pctExact >= 10 ? pctExact.toFixed(0) : pctExact.toFixed(1)}%`;
                    return (
                      <tr key={row.event || row.label}>
                        <td>{row.label || row.event || "—"}</td>
                        <td>{bigAmountFormat(val)}</td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div
                              role="progressbar"
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-valuenow={Math.round(pct)}
                              aria-label={`${row.label || row.event || "Event"}: ${pctLabel} of all events`}
                              style={{ flex: 1, height: 8, background: "#eef1f5", borderRadius: 4, overflow: "hidden" }}
                            >
                              <div
                                style={{
                                  width: `${Math.max(pct, 1.5)}%`,
                                  height: "100%",
                                  background: "#4c5fd5",
                                  borderRadius: 4,
                                }}
                              />
                            </div>
                            <div style={{ minWidth: 44, textAlign: "right", fontSize: 12, fontWeight: 600 }}>
                              {pctLabel}
                            </div>
                          </div>
                        </td>
                        <td>{revenue > 0 ? bigAmountFormat(revenue, getAuthUserCurrency()) : "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        <ListPaginationBar
          page={page}
          setPage={setPage}
          total={total}
          totalPages={totalPages}
          pageSize={pageSize}
          ariaLabel={paginationLabel}
        />
      </div>
    </div>
  );
}

function BreakdownTable({ rows, title }) {
  const { pageItems, page, setPage, total, totalPages, pageSize } = usePagedRows(rows);
  const data = Array.isArray(rows) ? rows : [];
  const max = data.length ? maxCount(data, "total") : 1;

  return (
    <div className="campaign-detail-breakdown">
      <h3 className="campaign-detail-section-title">{title}</h3>
      <div className="dashboard_app_table dashboard_app_table--list-records campaign-detail-breakdown-table">
        <div className="dashboard_table">
          <div className="table_scroll_wrapper">
            <div className="table_inner table_y_scroll">
              <table className="table_outer campaign-detail-dual-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Installs</th>
                    <th>Signups</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {!data.length ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-muted">
                        —
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((row) => {
                      const pct = Math.round((row.total / max) * 100);
                      return (
                        <tr key={row.label}>
                          <td>
                            <div className="campaign-detail-dual-name">
                              <span>{row.label}</span>
                              <div className="campaign-detail-bar-track campaign-detail-bar-track--inline">
                                <span style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </td>
                          <td>{bigAmountFormat(row.installs)}</td>
                          <td>{bigAmountFormat(row.signups)}</td>
                          <td>
                            <strong>{bigAmountFormat(row.total)}</strong>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <ListPaginationBar
            page={page}
            setPage={setPage}
            total={total}
            totalPages={totalPages}
            pageSize={pageSize}
            ariaLabel={`${title} pagination`}
          />
        </div>
      </div>
    </div>
  );
}

const todayDefault = getDateRangeForPreset("today");

export default function CampaignDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { appId: routeAppId, campaignId } = useParams();
  const isOrganicView = campaignId === "organic";
  const [searchParams, setSearchParams] = useSearchParams();

  const [appUuid, setAppUuid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [dateFrom, setDateFrom] = useState(
    () => searchParams.get("from") || todayDefault.dateFrom
  );
  const [dateTo, setDateTo] = useState(() => searchParams.get("to") || todayDefault.dateTo);
  const [datePreset, setDatePreset] = useState(() => {
    const from = searchParams.get("from") || todayDefault.dateFrom;
    const to = searchParams.get("to") || todayDefault.dateTo;
    return detectDateRangePreset(from, to);
  });
  const [draftDateFrom, setDraftDateFrom] = useState(() => searchParams.get("from") || todayDefault.dateFrom);
  const [draftDateTo, setDraftDateTo] = useState(() => searchParams.get("to") || todayDefault.dateTo);
  const [draftDatePreset, setDraftDatePreset] = useState(() => {
    const from = searchParams.get("from") || todayDefault.dateFrom;
    const to = searchParams.get("to") || todayDefault.dateTo;
    return detectDateRangePreset(from, to);
  });
  const [attrFilters, setAttrFilters] = useState(EMPTY_ATTR_FILTERS);
  const [draftAttrFilters, setDraftAttrFilters] = useState(EMPTY_ATTR_FILTERS);
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [recentPage, setRecentPage] = useState(1);
  const [recentLoading, setRecentLoading] = useState(false);
  // One independent paged "<metric label> Events" list per configured Event Metric — keyed by
  // metric id (the legacy no-metrics-configured default uses the key "default"). Each entry:
  // { rows, meta, page, loading }.
  const [metricLists, setMetricLists] = useState({});
  const metricListsRef = useRef(metricLists);
  useEffect(() => {
    metricListsRef.current = metricLists;
  }, [metricLists]);
  const [exportingList, setExportingList] = useState("");

  const loadApp = useCallback(async () => {
    const uuid = await fetchApplicationAppId(routeAppId);
    if (uuid) setAppUuid(uuid);
    return uuid;
  }, [routeAppId]);

  const buildAttrRequestBody = useCallback(
    (app_id, extra = {}) => {
      const body = {
        app_id,
        date_from: dateFrom,
        date_to: dateTo,
        timezone: getBrowserTimeZone() || undefined,
        ...attrFilters,
        ...extra,
      };
      if (!isOrganicView) body.campaignId = Number(campaignId);
      return body;
    },
    [attrFilters, campaignId, dateFrom, dateTo, isOrganicView]
  );

  const loadAttributionList = useCallback(
    async (uuid, { recentPage: recentPg } = {}) => {
      const app_id = uuid || appUuid;
      if (!app_id) return;
      if (!isOrganicView && !campaignId) return;

      setRecentLoading(true);
      try {
        const endpoint = `${Utils.baseurl}get/campaign/attribution-list`;
        const body = buildAttrRequestBody(app_id, {
          scope: isOrganicView ? "organic" : "campaign",
          list: "recent",
          recent_page: recentPg || 1,
          recent_page_size: RECENT_USERS_PAGE_SIZE,
        });

        const res = await fetchWithAuth(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (json.status) {
          setDetail((prev) => (prev ? { ...prev, ...json.data } : json.data));
        } else toast.error(json.message || "Could not load list");
      } finally {
        setRecentLoading(false);
      }
    },
    [appUuid, buildAttrRequestBody, campaignId, isOrganicView]
  );

  // One "<metric label> Events" table per configured Event Metric (see metricLists above) —
  // each independently paginated/exported, all sharing the same top filter bar. metricId null
  // is the legacy "no metrics configured" default, keyed "default".
  const loadEventMetricList = useCallback(
    async (uuid, metricId, page = 1) => {
      const app_id = uuid || appUuid;
      if (!app_id) return;
      if (!isOrganicView && !campaignId) return;

      const key = metricId == null ? "default" : String(metricId);
      setMetricLists((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), loading: true } }));
      try {
        const endpoint = `${Utils.baseurl}get/campaign/attribution-list`;
        const body = buildAttrRequestBody(app_id, {
          scope: isOrganicView ? "organic" : "campaign",
          list: "event_metric",
          event_metric_page: page,
          event_metric_page_size: RECENT_USERS_PAGE_SIZE,
          ...(metricId != null ? { metric_id: metricId } : {}),
        });

        const res = await fetchWithAuth(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (json.status) {
          setMetricLists((prev) => ({
            ...prev,
            [key]: {
              rows: json.data.event_metrics || [],
              meta: json.data.event_metrics_meta || {},
              page,
              loading: false,
            },
          }));
        } else {
          toast.error(json.message || "Could not load list");
          setMetricLists((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), loading: false } }));
        }
      } catch (error) {
        setMetricLists((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), loading: false } }));
      }
    },
    [appUuid, buildAttrRequestBody, campaignId, isOrganicView]
  );

  // Fetches every configured metric's list in parallel. resetPage=true (filter/date/campaign
  // change) always starts each one back at page 1; resetPage=false (manual refresh) re-fetches
  // whichever page each table is currently showing, read from a ref so this callback's own
  // identity never depends on metricLists (which would otherwise re-trigger the load effect).
  const loadAllEventMetricLists = useCallback(
    async (uuid, metrics, resetPage = true) => {
      const list = Array.isArray(metrics) ? metrics : [];
      if (!list.length) return;
      await Promise.all(
        list.map((m) => {
          const key = m.id == null ? "default" : String(m.id);
          const page = resetPage ? 1 : metricListsRef.current[key]?.page || 1;
          return loadEventMetricList(uuid, m.id ?? null, page);
        })
      );
    },
    [loadEventMetricList]
  );

  // Combined fetch for EVERY configured metric's first page in ONE request — used for a fresh
  // load (initial page load, or a filter/date/campaign change) where the old N-parallel-calls
  // approach redundantly re-resolved identical shared setup (app/campaign lookup, identity
  // scope) N times per load. Backend always returns page 1 per metric here; a manual refresh
  // that needs to keep each table on its current page still uses loadAllEventMetricLists above.
  const loadEventMetricListsAll = useCallback(
    async (uuid) => {
      const app_id = uuid || appUuid;
      if (!app_id) return;
      if (!isOrganicView && !campaignId) return;

      setMetricLists((prev) => {
        const next = {};
        for (const key of Object.keys(prev)) next[key] = { ...prev[key], loading: true };
        return next;
      });
      try {
        const endpoint = `${Utils.baseurl}get/campaign/attribution-list`;
        const body = buildAttrRequestBody(app_id, {
          scope: isOrganicView ? "organic" : "campaign",
          list: "event_metric_all",
          event_metric_page_size: RECENT_USERS_PAGE_SIZE,
        });
        const res = await fetchWithAuth(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (json.status) {
          const lists = Array.isArray(json.data.event_metric_lists) ? json.data.event_metric_lists : [];
          const next = {};
          for (const entry of lists) {
            const key = entry.metric?.id == null ? "default" : String(entry.metric.id);
            next[key] = { rows: entry.rows || [], meta: entry.meta || {}, page: 1, loading: false };
          }
          setMetricLists(next);
        } else {
          toast.error(json.message || "Could not load list");
          setMetricLists((prev) => {
            const next = {};
            for (const key of Object.keys(prev)) next[key] = { ...prev[key], loading: false };
            return next;
          });
        }
      } catch (error) {
        setMetricLists((prev) => {
          const next = {};
          for (const key of Object.keys(prev)) next[key] = { ...prev[key], loading: false };
          return next;
        });
      }
    },
    [appUuid, buildAttrRequestBody, campaignId, isOrganicView]
  );

  const loadDetail = useCallback(
    async (uuid) => {
      const app_id = uuid || appUuid;
      if (!app_id) return null;
      if (!isOrganicView && !campaignId) return null;

      setLoading(true);
      try {
        const endpoint = isOrganicView
          ? `${Utils.baseurl}get/campaign/organic/detail`
          : `${Utils.baseurl}get/campaign/detail`;

        const res = await fetchWithAuth(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildAttrRequestBody(app_id)),
        });
        const json = await res.json();
        if (json.status) {
          setDetail((prev) => ({
            ...json.data,
            recent_users: prev?.recent_users,
            recent_users_meta: prev?.recent_users_meta,
            metrics: prev?.metrics,
          }));
          return json.data;
        }
        toast.error(json.message || "Could not load details");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [appUuid, buildAttrRequestBody, campaignId, isOrganicView]
  );

  const loadPage = useCallback(
    async (uuid) => {
      const app_id = uuid || appUuid;
      if (!app_id) return;
      // The combined event_metric_all fetch resolves its own metric configs server-side, so it
      // no longer needs to wait on loadDetail's response first — all three run together.
      await Promise.all([
        loadDetail(app_id),
        loadAttributionList(app_id, { recentPage: 1 }),
        loadEventMetricListsAll(app_id),
      ]);
    },
    [appUuid, loadAttributionList, loadDetail, loadEventMetricListsAll]
  );

  // Each attribution field is named after where its data actually comes from, and all of
  // them are app-wide now — none scoped by campaign/organic or by the date filter, so
  // switching campaigns or narrowing the date range never hides a known value:
  //  - event: event_type lives on campaign_attribution itself — campaign-event.
  //  - state/city: plain app_users columns.
  //  - platform: lives on the subscription table (device_os/type), not app_users —
  //    subscription-platform.
  //  - gender: an app_users.tags value — the generic app-user-tag-value type (field=gender),
  //    reusable for any other tag the same way.
  const buildFilterFetch = useCallback(
    (field) =>
      ({ search, ids }) => {
        const config = ATTR_FILTER_AJAX_CONFIG[field] || {};
        return fetchAjaxListData({
          type: config.type || `app-user-${field}`,
          q: search,
          id: ids,
          extra: {
            app_id: appUuid,
            ...config.extra,
          },
          mode: "options",
        });
      },
    [appUuid]
  );

  const refreshAll = useCallback(async () => {
    const uuid = (await loadApp()) || appUuid;
    const [detailData] = await Promise.all([
      loadDetail(uuid),
      loadAttributionList(uuid, { recentPage }),
    ]);
    const metrics = Array.isArray(detailData?.metrics)
      ? detailData.metrics
      : Array.isArray(detailData?.summary?.metrics)
        ? detailData.summary.metrics
        : [];
    // Re-fetch each table's current page, not reset to 1 — a manual refresh shouldn't jump
    // the user back to the first page of a list they've already paged through.
    await loadAllEventMetricLists(uuid, metrics, false);
  }, [appUuid, loadAllEventMetricLists, loadApp, loadAttributionList, loadDetail, recentPage]);

  useEffect(() => {
    setRecentPage(1);
    let cancelled = false;
    (async () => {
      const uuid = await loadApp();
      if (cancelled || !uuid) return;
      await loadPage(uuid);
    })();
    return () => {
      cancelled = true;
    };
  }, [routeAppId, campaignId, dateFrom, dateTo, attrFilters, loadApp, loadPage]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("from", dateFrom);
    next.set("to", dateTo);
    setSearchParams(next, { replace: true });
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateRangeChange = ({ dateFrom: from, dateTo: to, preset }) => {
    setDraftDateFrom(from || toLocalIsoDate());
    setDraftDateTo(to || toLocalIsoDate());
    if (preset) setDraftDatePreset(preset);
  };

  const updateAttrFilter = (key, value) => {
    setDraftAttrFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onApplyFilters = () => {
    setRecentPage(1);
    setDateFrom(draftDateFrom);
    setDateTo(draftDateTo);
    setDatePreset(draftDatePreset);
    setAttrFilters({ ...draftAttrFilters });
    setFilterAccordionOpen(filtersObjectHasAnyValue(draftAttrFilters));
  };

  const onResetFilters = () => {
    const range = getDateRangeForPreset("today");
    setRecentPage(1);
    setDraftDateFrom(range.dateFrom);
    setDraftDateTo(range.dateTo);
    setDraftDatePreset(range.preset || "today");
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    setDatePreset(range.preset || "today");
    setDraftAttrFilters(EMPTY_ATTR_FILTERS);
    setAttrFilters(EMPTY_ATTR_FILTERS);
    setFilterAccordionOpen(false);
  };

  const handleRecentPageChange = (page) => {
    if (page === recentPage) return;
    setRecentPage(page);
    loadAttributionList(appUuid, { list: "recent", recentPage: page });
  };

  const handleEventMetricPageChange = (metricId, page) => {
    const key = metricId == null ? "default" : String(metricId);
    if (metricLists[key]?.page === page) return;
    loadEventMetricList(appUuid, metricId, page);
  };

  const handleExportAttribution = async (list, metricId) => {
    if (exportingList || !appUuid) return;
    const exportKey = list === "event_metric" ? `event_metric-${metricId == null ? "default" : metricId}` : list;
    setExportingList(exportKey);
    try {
      const { status, message } = await exportListCsv(
        "get/campaign/attribution-list",
        buildAttrRequestBody(appUuid, {
          scope: isOrganicView ? "organic" : "campaign",
          list,
          ...(list === "event_metric" && metricId != null ? { metric_id: metricId } : {}),
        }),
        list === "event_metric" ? "event-metrics.csv" : "recent-attributed-users.csv"
      );
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setExportingList("");
    }
  };

  const campaign = detail?.campaign;
  const summary = detail?.summary || {};
  const clicksBd = detail?.clicks_breakdown || {};
  const bd = detail?.breakdowns || {};
  const eventCounts = detail?.event_counts || [];
  const summaryMetrics = isOrganicView ? ORGANIC_SUMMARY_METRICS : CAMPAIGN_SUMMARY_METRICS;
  // One independent "<metric label> Events" table per configured Event Metric — see
  // metricLists above. No single "active" metric anymore, so no selector for it either.
  // detail.metrics (from the attribution-list response) can be wiped when loadDetail's response
  // replaces `detail`; summary.metrics always comes with the detail payload and also carries the
  // legacy "default" entry (id null) when the app has no Event Metric configured — so fall back
  // to it, otherwise the default booking list vanishes while the tiles above still show.
  const detailMetricsList =
    Array.isArray(detail?.metrics) && detail.metrics.length
      ? detail.metrics
      : Array.isArray(summary.metrics)
        ? summary.metrics
        : [];
  const pageTitle = isOrganicView
    ? "Organic attribution"
    : loading && !campaign
      ? "Campaign details"
      : campaign?.name;

  const handleBack = () => {
    const backPath = resolveCampaignDetailBackPath({
      routeAppId,
      locationState: location.state,
      searchParams,
      dateFrom,
      dateTo,
    });
    navigate(backPath);
  };

  const hasClickDevice = (clicksBd.by_device || []).length > 0;
  const hasClickCountry = (clicksBd.by_country || []).length > 0;
  const hasClickCity = (clicksBd.by_city || []).length > 0;

  const recentMeta = detail?.recent_users_meta || {};
  const recentTotal = Number(recentMeta.total) || 0;
  const recentPageSize = Number(recentMeta.pageSize) || RECENT_USERS_PAGE_SIZE;
  const recentTotalPages =
    Number(recentMeta.totalPages) || Math.max(1, Math.ceil(recentTotal / recentPageSize) || 1);
  const recentSafePage = Math.min(recentPage, recentTotalPages);



  return (
    <div className="dashboard_right">
      <div className="dashboard_inner campaign-detail-page">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1 className="mb-1">{pageTitle}</h1>
          </div>
          <div className="add_app d-flex gap-2 flex-wrap">
            <button type="button" className="add_app_btn btn_theme_secondary" onClick={refreshAll}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button
              type="button"
              className="add_app_btn_secondary btn_theme_secondary"
              onClick={handleBack}
            >
              <span>Back</span>
            </button>
          </div>
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((o) => !o)}
            aria-expanded={filterAccordionOpen}
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown
              className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`}
              size={22}
            />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="campaign-filter-bar">
                <div className="campaign-filter-item campaign-filter-item--date-range">
                  <DateRangePicker
                    dateFrom={draftDateFrom}
                    dateTo={draftDateTo}
                    preset={draftDatePreset}
                    presets={DATE_RANGE_PRESETS}
                    allowClear={false}
                    onChange={handleDateRangeChange}
                  />
                </div>

                {ATTR_FILTER_FIELDS.map(({ key, label, placeholder }) => (
                  <div className="campaign-filter-item" key={key}>
                    <label>{label}</label>
                    {ATTR_FILTER_INPUT_KEYS.has(key) ? (
                      <input
                        type="text"
                        className="form-control"
                        value={draftAttrFilters[key] || ""}
                        placeholder={placeholder}
                        onChange={(e) => updateAttrFilter(key, e.target.value)}
                      />
                    ) : (
                      <AsyncSearchSelect
                        classNamePrefix="ns-select"
                        isClearable
                        placeholder={placeholder}
                        enabled={Boolean(appUuid)}
                        fetchOptions={buildFilterFetch(key)}
                        value={draftAttrFilters[key] || null}
                        onChange={(opt) => updateAttrFilter(key, opt?.value ?? "")}
                        styles={selectStyles}
                      />
                    )}
                  </div>
                ))}
                <div className="campaign-filter-item campaign-filter-item--actions">
                  <div className="add_app">
                    <button type="button" className="add_app_btn btn_theme_primary" title="Apply filters" onClick={onApplyFilters}>
                      <MdSearch />
                      <span>Search</span>
                    </button>
                    <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Reset filters" onClick={onResetFilters}>
                      <MdRefresh />
                      <span>Reset</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="campaign-summary-row">
          {summaryMetrics.map(({ key, label, suffix, color }) => {
            const raw = summary[key] ?? 0;
            const display = loading
              ? "—"
              : suffix === "%"
                ? `${raw}${suffix}`
                : bigAmountFormat(raw);
            return (
              <div key={key} className="campaign-summary-card">
                <span className="campaign-summary-label">{label}</span>
                <strong className="campaign-summary-value" style={{ color }}>
                  {display}
                </strong>
              </div>
            );
          })}
        </div>

        {/* Event metrics — one labeled row (this page is scoped to a single source: one
            campaign link, or organic) listing every configured metric's Count + Amount tile
            together, instead of scattering metrics among the generic tiles above. */}
        {/* No Campaign/Organic/Total scope label here — this page is always scoped to a single
            source, unlike the Campaign Dashboard which rolls up all three. */}
        {(summary.metrics || []).length > 0 && (
          <div>
            <h3 className="campaign-event-metrics-title">Event metrics</h3>
            <div className="campaign-summary-row">
              {summary.metrics.map((m) => (
                <React.Fragment key={`metric-${m.id ?? "default"}`}>
                  {m.show_count !== false && (
                    <div className="campaign-summary-card">
                      <span className="campaign-summary-label">{composeEventMetricLabel(m.label, "Count")}</span>
                      <strong className="campaign-summary-value" style={{ color: "#0b5f8a" }}>
                        {loading ? "—" : bigAmountFormat(m.count || 0)}
                      </strong>
                    </div>
                  )}
                  {m.show_amount !== false && (
                    <div className="campaign-summary-card">
                      <span className="campaign-summary-label">{m.label}</span>
                      <strong className="campaign-summary-value" style={{ color: "#0d6e4f" }}>
                        {loading ? "—" : bigAmountFormat(m.amount || 0, getAuthUserCurrency())}
                      </strong>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="campaign-detail-section-title">Events — count &amp; revenue</h3>
          {loading ? (
            <div className="campaign-detail-bar-panel">
              <p className="text-muted mb-0 px-2 py-2">Loading…</p>
            </div>
          ) : (
            <EventCountsTable rows={eventCounts} paginationLabel="Event counts pagination" />
          )}
        </div>

        {detailMetricsList.map((metric) => {
          const metricKey = metric.id == null ? "default" : String(metric.id);
          const metricState = metricLists[metricKey] || {};
          const metricRows = metricState.rows || [];
          const metricMetaForList = metricState.meta || {};
          const metricTotal = Number(metricMetaForList.total) || 0;
          const metricPageSize = Number(metricMetaForList.pageSize) || RECENT_USERS_PAGE_SIZE;
          const metricTotalPages =
            Number(metricMetaForList.totalPages) || Math.max(1, Math.ceil(metricTotal / metricPageSize) || 1);
          const metricPage = Math.min(metricState.page || 1, metricTotalPages);
          const metricLoading = Boolean(metricState.loading);
          const metricLabel = metric.label || EVENT_METRIC_OPTION.label;
          const exportKey = `event_metric-${metricKey}`;

          return (
            <React.Fragment key={metricKey}>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <h3 className="campaign-detail-section-title mb-0">{composeEventMetricLabel(metricLabel, "Events")}</h3>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="add_app_btn_secondary btn_theme_secondary"
                    title="Export CSV for applied filters"
                    onClick={() => handleExportAttribution("event_metric", metric.id ?? null)}
                    disabled={Boolean(exportingList)}
                  >
                    <MdFileDownload />
                    <span>{exportingList === exportKey ? "Exporting…" : "Export"}</span>
                  </button>
                </div>
              </div>
              <div className="dashboard_app_table dashboard_app_table--list-records campaign-detail-recent-table">
                <div className="dashboard_table">
                  <div className="table_scroll_wrapper">
                    <div className="table_inner table_y_scroll">
                      <table className="table_outer">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Event</th>
                            <th>{composeEventMetricLabel(metricLabel)}</th>
                            <th>Order ID</th>
                            <th>NotifySphere ID</th>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Email</th>
                            <th>Location</th>
                            <th>Platform</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading || metricLoading ? (
                            <tr>
                              <td colSpan="10" className="text-center py-4">
                                Loading…
                              </td>
                            </tr>
                          ) : !metricRows.length ? (
                            <tr>
                              <td colSpan="10" className="text-center py-4 text-muted">
                                —
                              </td>
                            </tr>
                          ) : (
                            metricRows.map((u) => (
                              <tr key={`event_metric-${metricKey}-${u.attribution_id}-${u.event_type}`}>
                                <td>{u.event_at ? formatDateTime(u.event_at) : "—"}</td>
                                <td>
                                  {u.event_type
                                    ? u.event_type.charAt(0).toUpperCase() + u.event_type.slice(1)
                                    : "—"}
                                </td>
                                <td>{bigAmountFormat(u.event_metric, getAuthUserCurrency())}</td>
                                <td>{u.order_id || "—"}</td>
                                <td>
                                  {u.notify_sphere_id && u.app_user_id != null ? (
                                    <AppUserProfileLink routeAppId={routeAppId} appUserId={u.app_user_id}>
                                      {u.notify_sphere_id}
                                    </AppUserProfileLink>
                                  ) : (
                                    u.notify_sphere_id || "—"
                                  )}
                                </td>
                                <td>{u.user_name || "—"}</td>
                                <td>{u.phone || "—"}</td>
                                <td>{u.email || "—"}</td>
                                <td>{u.location_display || "—"}</td>
                                <td>{u.platform || "—"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <ListPaginationBar
                    page={metricPage}
                    setPage={(page) => handleEventMetricPageChange(metric.id ?? null, page)}
                    total={metricTotal}
                    totalPages={metricTotalPages}
                    pageSize={metricPageSize}
                    ariaLabel={`${metricLabel} events pagination`}
                    loading={metricLoading}
                  />
                </div>
              </div>
            </React.Fragment>
          );
        })}
        {!isOrganicView && (hasClickDevice || hasClickCountry) && (
          <div className="campaign-detail-grid-2">
            {hasClickDevice ? (
              <div>
                <h3 className="campaign-detail-section-title">Clicks by device</h3>
                <div className="campaign-detail-bar-panel">
                  <SimpleBarList
                    rows={clicksBd.by_device}
                    paginationLabel="Clicks by device pagination"
                  />
                </div>
              </div>
            ) : null}
            {hasClickCountry ? (
              <div>
                <h3 className="campaign-detail-section-title">Clicks by country</h3>
                <div className="campaign-detail-bar-panel">
                  <SimpleBarList
                    rows={clicksBd.by_country}
                    paginationLabel="Clicks by country pagination"
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}

        {!isOrganicView && hasClickCity && (
          <div>
            <h3 className="campaign-detail-section-title">Click cities (from link click IP)</h3>
            <div className="campaign-detail-bar-panel">
              <SimpleBarList rows={clicksBd.by_city} paginationLabel="Click cities pagination" />
            </div>
          </div>
        )}

        <div className="campaign-detail-grid-2">
          <BreakdownTable rows={bd.by_state} title="Area — State" />
          <BreakdownTable rows={bd.by_city} title="Area — City" />
        </div>

        <div className="campaign-detail-grid-2">
          <BreakdownTable rows={bd.by_platform} title="Device — Platform - OS ( tags)" />
          <BreakdownTable rows={bd.by_gender} title="Audience — Gender (tags)" />
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h3 className="campaign-detail-section-title mb-0">Recent attributed users</h3>
          <button
            type="button"
            className="add_app_btn_secondary btn_theme_secondary"
            title="Export CSV for applied filters"
            onClick={() => handleExportAttribution("recent")}
            disabled={Boolean(exportingList)}
          >
            <MdFileDownload />
            <span>{exportingList === "recent" ? "Exporting…" : "Export"}</span>
          </button>
        </div>
        <div className="dashboard_app_table dashboard_app_table--list-records campaign-detail-recent-table">
          <div className="dashboard_table">
            <div className="table_scroll_wrapper">
              <div className="table_inner table_y_scroll">
                <table className="table_outer">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Event</th>
                      <th>NotifySphere ID</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Location</th>
                      <th>Lat / Long</th>
                      <th>Platform</th>
                      <th>Gender</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading || recentLoading ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          Loading…
                        </td>
                      </tr>
                    ) : !detail?.recent_users?.length ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4 text-muted">
                          —
                        </td>
                      </tr>
                    ) : (
                      detail.recent_users.map((u) => (
                        <tr key={`${u.attribution_id}-${u.event_type}`}>
                          <td>{u.event_at ? formatDateTime(u.event_at) : "—"}</td>
                          <td>
                            {u.event_type
                              ? u.event_type.charAt(0).toUpperCase() + u.event_type.slice(1)
                              : "—"}
                          </td>
                          <td>
                            {u.notify_sphere_id && u.app_user_id != null ? (
                              <AppUserProfileLink routeAppId={routeAppId} appUserId={u.app_user_id}>
                                {u.notify_sphere_id}
                              </AppUserProfileLink>
                            ) : (
                              u.notify_sphere_id || "—"
                            )}
                          </td>
                          <td>{u.user_name || "—"}</td>
                          <td>{u.phone || "—"}</td>
                          <td>{u.email || "—"}</td>
                          <td>{u.location_display || "—"}</td>
                          <td>
                            {u.lat && u.long ? (
                              <span title={`${u.lat}, ${u.long}`}>
                                {Number(u.lat).toFixed(4)}, {Number(u.long).toFixed(4)}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>{u.platform || "—"}</td>
                          <td>{u.gender || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <ListPaginationBar
              page={recentSafePage}
              setPage={handleRecentPageChange}
              total={recentTotal}
              totalPages={recentTotalPages}
              pageSize={recentPageSize}
              ariaLabel="Recent attributed users pagination"
              loading={recentLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
